/**
 * VehicleDocumentsTab - Enhanced document tab for vehicle detail modal
 *
 * Features:
 * - Accordion-based document organization by folder category
 * - Compliance status badges and summary bar
 * - Active documents section (current vehicle documents)
 * - Archived documents section (if vehicle has been archived)
 * - DB-backed accordion state (closed by default)
 * - Document upload, view, download functionality
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../../../api/core.js';
import { useFormat } from '../../../i18n/format.js';
import {
  FileText,
  ClipboardCheck,
  Shield,
  FileSignature,
  Wrench,
  AlertTriangle,
  Folder,
  Upload,
  Download,
  Eye,
  Trash2,
  Archive,
  ChevronDown,
  ChevronRight,
  Package
} from 'lucide-react';
import { useAccordionState } from '../../../hooks/useAccordionState.js';
import { documentAPI } from '../../../api.js';
import DocumentStatusBadge from '../../common/DocumentStatusBadge.jsx';
import ComplianceSummaryBar from '../../common/ComplianceSummaryBar.jsx';
import DocumentDropZone from '../../common/DocumentDropZone.jsx';
import DocumentValidationModal from '../../documents/DocumentValidationModal.jsx';

// Document folder configuration
const FOLDER_CONFIG = {
  '10_REGISTRATION': {
    key: 'registration',
    label: 'Carte Grise & Immatriculation',
    icon: FileText,
    color: 'blue',
    required: true,
    documentTypes: ['VEHICLE_REGISTRATION', 'REGISTRATION_CERTIFICATE']
  },
  '20_TECHNICAL_INSPECTION': {
    key: 'technical',
    label: 'Controle Technique',
    icon: ClipboardCheck,
    color: 'purple',
    required: true,
    renewable: true,
    documentTypes: ['TECHNICAL_INSPECTION', 'SAFETY_INSPECTION']
  },
  '30_INSURANCE': {
    key: 'insurance',
    label: 'Assurance',
    icon: Shield,
    color: 'emerald',
    required: true,
    renewable: true,
    documentTypes: ['VEHICLE_INSURANCE_CARD', 'INSURANCE_CERTIFICATE']
  },
  '40_LEASING_RENTAL': {
    key: 'leasing',
    label: 'Contrats (Leasing/Location)',
    icon: FileSignature,
    color: 'amber',
    required: false,
    conditional: true,
    documentTypes: ['LEASE_CONTRACT', 'RENTAL_AGREEMENT', 'LEASING_CONTRACT']
  },
  '50_MAINTENANCE_INVOICES': {
    key: 'maintenance',
    label: 'Factures Maintenance',
    icon: Wrench,
    color: 'blue',
    required: false,
    documentTypes: ['MAINTENANCE_INVOICE', 'REPAIR_INVOICE', 'TIRE_INVOICE', 'SERVICE_INVOICE']
  },
  '60_CLAIMS': {
    key: 'claims',
    label: 'Dossiers Sinistres',
    icon: AlertTriangle,
    color: 'red',
    required: false,
    documentTypes: ['CLAIM_REPORT', 'CLAIM_INVOICE']
  },
  '90_MISC': {
    key: 'misc',
    label: 'Autres Documents',
    icon: Folder,
    color: 'gray',
    required: false,
    documentTypes: ['VEHICLE_PHOTO', 'VEHICLE_MISC', 'OTHER']
  }
};

// Map document type to folder
function getDocumentFolder(documentType) {
  for (const [folderCode, config] of Object.entries(FOLDER_CONFIG)) {
    if (config.documentTypes.some(t =>
      documentType?.toUpperCase().includes(t) || t.includes(documentType?.toUpperCase())
    )) {
      return folderCode;
    }
  }
  return '90_MISC';
}

// Calculate folder status based on documents
function calculateFolderStatus(folder, documents, vehicleData) {
  const config = FOLDER_CONFIG[folder];
  if (!config) return { status: 'empty', docs: 0 };

  const folderDocs = documents.filter(d => getDocumentFolder(d.document_type) === folder);
  const docCount = folderDocs.length;

  // Check for conditional folder (leasing only if lease vehicle)
  if (config.conditional) {
    const isLeaseVehicle = ['lease', 'lld', 'loa', 'rental'].includes(
      vehicleData?.acquisition_type?.toLowerCase()
    );
    if (!isLeaseVehicle) {
      return { status: 'not_applicable', docs: 0 };
    }
  }

  if (docCount === 0) {
    return config.required
      ? { status: 'missing', docs: 0 }
      : { status: 'empty', docs: 0 };
  }

  // Check for expiring documents (renewable folders)
  if (config.renewable) {
    const now = new Date();
    const warningDays = folder === '30_INSURANCE' ? 30 : 60;

    for (const doc of folderDocs) {
      const expiryDate = doc.valid_to || doc.expiry_date;
      if (expiryDate) {
        const expiry = new Date(expiryDate);
        const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

        if (daysUntil < 0) {
          return { status: 'expired', docs: docCount, expiresIn: daysUntil };
        }
        if (daysUntil <= warningDays) {
          return { status: 'expiring', docs: docCount, expiresIn: daysUntil };
        }
      }
    }
  }

  // Check validation status
  const pendingDocs = folderDocs.filter(d => d.validation_status === 'pending');
  const rejectedDocs = folderDocs.filter(d => d.validation_status === 'rejected');

  if (rejectedDocs.length > 0) {
    return { status: 'validation_rejected', docs: docCount };
  }
  if (pendingDocs.length > 0) {
    return { status: 'pending_validation', docs: docCount };
  }

  return { status: config.required ? 'complete' : 'has_docs', docs: docCount };
}

// Calculate overall compliance
function calculateCompliance(documents, vehicleData) {
  const folders = Object.keys(FOLDER_CONFIG);
  const results = [];
  let requiredCount = 0;
  let completeCount = 0;
  const issues = [];

  for (const folder of folders) {
    const config = FOLDER_CONFIG[folder];
    const status = calculateFolderStatus(folder, documents, vehicleData);

    if (status.status === 'not_applicable') continue;

    results.push({
      code: folder,
      key: config.key,
      label: config.label,
      ...status
    });

    if (config.required) {
      requiredCount++;
      if (status.status === 'complete') {
        completeCount++;
      } else if (status.status === 'expiring') {
        completeCount += 0.8;
        issues.push({
          type: 'expiring',
          message: `${config.label} expire dans ${status.expiresIn} jour(s)`,
          severity: 'warning'
        });
      } else if (status.status === 'expired') {
        issues.push({
          type: 'expired',
          message: `${config.label} est expire`,
          severity: 'critical'
        });
      } else if (status.status === 'missing') {
        issues.push({
          type: 'missing',
          message: `${config.label} est manquant`,
          severity: 'error'
        });
      } else if (status.status === 'pending_validation') {
        completeCount += 0.5;
        issues.push({
          type: 'pending',
          message: `${config.label} en attente de validation`,
          severity: 'warning'
        });
      }
    }
  }

  const completeness = requiredCount > 0
    ? Math.round((completeCount / requiredCount) * 100)
    : 100;

  return { folders: results, completeness, requiredCount, completeCount: Math.floor(completeCount), issues };
}

// Group documents by folder
function groupDocumentsByFolder(documents) {
  const grouped = {};
  for (const folder of Object.keys(FOLDER_CONFIG)) {
    grouped[folder] = [];
  }

  for (const doc of documents) {
    const folder = getDocumentFolder(doc.document_type);
    if (grouped[folder]) {
      grouped[folder].push(doc);
    } else {
      grouped['90_MISC'].push(doc);
    }
  }

  return grouped;
}

/**
 * VehicleDocumentsTab
 */
export default function VehicleDocumentsTab({ entity: vehicle, onMessage }) {
  const { fmt } = useFormat();
  // State
  const [documents, setDocuments] = useState([]);
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [archiveInfo, setArchiveInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);

  // DB-backed accordion state (closed by default)
  const {
    isExpanded,
    toggleGroup
  } = useAccordionState({ viewCode: 'vehicle_documents_tab' });

  // Fetch active documents
  const fetchDocuments = useCallback(async () => {
    if (!vehicle?.id) return;

    try {
      setLoading(true);
      const res = await documentAPI.getForVehicle(vehicle.id);
      setDocuments(res.data || res || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      onMessage?.({ title: 'Erreur', message: 'Impossible de charger les documents', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [vehicle?.id, onMessage]);

  // Check for archived documents
  const fetchArchivedDocuments = useCallback(async () => {
    if (!vehicle?.id) return;

    try {

      // Check if vehicle has an archive
      const archiveRes = await fetch(`/api/v1/vehicle-archives?original_vehicle_id=${vehicle.id}`, {
        headers: getAuthHeaders()
      });
      const archiveData = await archiveRes.json();

      if (archiveData.ok && archiveData.data?.length > 0) {
        const archive = archiveData.data[0];
        setArchiveInfo(archive);

        // Fetch archived documents
        const docsRes = await fetch(`/api/v1/vehicle-archives/${archive.id}/documents`, {
          headers: getAuthHeaders()
        });
        const docsData = await docsRes.json();

        if (docsData.ok) {
          setArchivedDocs(docsData.data || []);
        }
      }
    } catch (err) {
      console.error('Error fetching archived documents:', err);
    }
  }, [vehicle?.id]);

  useEffect(() => {
    fetchDocuments();
    fetchArchivedDocuments();
  }, [fetchDocuments, fetchArchivedDocuments]);

  // Handle upload complete
  const handleUploadComplete = (uploadedDocs) => {
    setShowUpload(false);
    fetchDocuments();
    onMessage?.({
      title: 'Document ajoute',
      message: `${uploadedDocs.length} document(s) ajoute(s) avec succes`,
      type: 'success'
    });
  };

  // Handle document download
  const handleDownload = (doc) => {
    documentAPI.download(doc.id, doc.original_filename || doc.filename);
  };

  // Handle document delete
  const handleDelete = async (doc) => {
    if (!window.confirm('Supprimer ce document ?')) return;

    try {
      await documentAPI.delete(doc.id);
      fetchDocuments();
      onMessage?.({ title: 'Supprime', message: 'Document supprime', type: 'success' });
    } catch (err) {
      onMessage?.({ title: 'Erreur', message: 'Impossible de supprimer', type: 'error' });
    }
  };

  // Calculate compliance
  const compliance = calculateCompliance(documents, vehicle);
  const groupedDocs = groupDocumentsByFolder(documents);
  const groupedArchivedDocs = groupDocumentsByFolder(archivedDocs);

  // Reference ID for uploads
  const referenceId = vehicle?.id ? `VHC_${String(vehicle.id).padStart(6, '0')}` : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement des documents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with upload button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Documents du Vehicule</h3>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showUpload
              ? 'bg-gray-200 text-gray-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Upload className="w-4 h-4" />
          {showUpload ? 'Fermer' : 'Ajouter un document'}
        </button>
      </div>

      {/* Upload zone */}
      {showUpload && (
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <DocumentDropZone
            scope="VEHICLE"
            referenceId={referenceId}
            allowedTypes={Object.values(FOLDER_CONFIG).flatMap(c => c.documentTypes)}
            maxFiles={10}
            maxSizeBytes={50 * 1024 * 1024}
            onUploadComplete={handleUploadComplete}
            onError={(err) => onMessage?.({ title: 'Erreur', message: err.message, type: 'error' })}
          />
        </div>
      )}

      {/* Compliance summary bar */}
      <ComplianceSummaryBar
        completeness={compliance.completeness}
        requiredCount={compliance.requiredCount}
        completeCount={compliance.completeCount}
        issues={compliance.issues}
      />

      {/* Active documents accordion */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleGroup('active')}
          className="w-full px-4 py-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded('active') ? (
              <ChevronDown className="w-5 h-5 text-blue-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-blue-600" />
            )}
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900">Documents Actifs</span>
            <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-sm">
              {documents.length}
            </span>
          </div>
        </button>

        {isExpanded('active') && (
          <div className="p-4 space-y-3">
            {compliance.folders.map(folder => {
              const config = FOLDER_CONFIG[folder.code];
              if (!config || folder.status === 'not_applicable') return null;

              const FolderIcon = config.icon;
              const folderDocs = groupedDocs[folder.code] || [];
              const folderKey = `active:${folder.key}`;

              return (
                <div key={folder.code} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleGroup(folderKey)}
                    className={`w-full px-4 py-2 flex items-center justify-between bg-${config.color}-50 hover:bg-${config.color}-100 transition-colors`}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded(folderKey) ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                      <FolderIcon className={`w-4 h-4 text-${config.color}-600`} />
                      <span className="font-medium text-gray-800">{config.label}</span>
                      <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full text-xs">
                        {folder.docs}
                      </span>
                    </div>
                    <DocumentStatusBadge
                      status={folder.status}
                      expiresIn={folder.expiresIn}
                    />
                  </button>

                  {isExpanded(folderKey) && (
                    <div className="p-3 bg-white space-y-2">
                      {folderDocs.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Aucun document dans cette categorie</p>
                      ) : (
                        folderDocs.map(doc => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="text-sm truncate">
                                {doc.original_filename || doc.filename || 'Document'}
                              </span>
                              {doc.validation_status && (
                                <DocumentStatusBadge
                                  status={doc.validation_status === 'validated' ? 'complete' : 'pending_validation'}
                                  compact
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setViewerDoc(doc)}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Voir"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownload(doc)}
                                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                                title="Telecharger"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(doc)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Archived documents (if exists) */}
      {archiveInfo && archivedDocs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleGroup('archived')}
            className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              {isExpanded('archived') ? (
                <ChevronDown className="w-5 h-5 text-slate-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-600" />
              )}
              <Archive className="w-5 h-5 text-slate-600" />
              <span className="font-semibold text-slate-900">Documents Archives</span>
              <span className="bg-slate-200 text-slate-800 px-2 py-0.5 rounded-full text-sm">
                {archivedDocs.length}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Package className="w-4 h-4" />
              <span>Archive du {fmt.date(archiveInfo.archived_at)}</span>
            </div>
          </button>

          {isExpanded('archived') && (
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-500 mb-3">
                Documents de la periode d'exploitation du vehicule
              </p>

              {Object.entries(FOLDER_CONFIG).map(([folderCode, config]) => {
                const folderDocs = groupedArchivedDocs[folderCode] || [];
                if (folderDocs.length === 0) return null;

                const FolderIcon = config.icon;
                const folderKey = `archived:${config.key}`;

                return (
                  <div key={folderCode} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(folderKey)}
                      className="w-full px-4 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded(folderKey) ? (
                          <ChevronDown className="w-4 h-4 text-gray-600" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        )}
                        <FolderIcon className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">{config.label}</span>
                        <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">
                          {folderDocs.length}
                        </span>
                      </div>
                    </button>

                    {isExpanded(folderKey) && (
                      <div className="p-3 bg-white space-y-2">
                        {folderDocs.map((doc, idx) => (
                          <div
                            key={doc.id || idx}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-600 truncate">
                                {doc.document_name || doc.original_filename || 'Document archive'}
                              </span>
                              <span className="text-xs text-gray-400">
                                {doc.document_date && fmt.date(doc.document_date)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Document validation/viewer modal */}
      <DocumentValidationModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        doc={viewerDoc}
        entityData={vehicle}
        viewOnly={viewerDoc?.validation_status === 'validated'}
        onValidated={() => {
          setViewerDoc(null);
          fetchDocuments();
          onMessage?.({ title: 'Valide', message: 'Document valide avec succes', type: 'success' });
        }}
      />
    </div>
  );
}
